import { Box, Skeleton, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { FinanceSummary, Metric, formatNaira } from "services/finance";

const cardSx = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E4E7EC",
  borderRadius: "12px",
  p: "24px",
};

const ChangeChip = ({ change }: { change: number | null }) => {
  if (change === null || change === undefined) {
    return (
      <Typography fontSize={12} color="#667085">
        No previous period data
      </Typography>
    );
  }
  const up = change >= 0;
  const Icon = up ? NorthEastIcon : SouthEastIcon;
  return (
    <Box display="flex" alignItems="center" gap="8px">
      <Box
        display="inline-flex"
        alignItems="center"
        gap="2px"
        px="6px"
        py="1px"
        borderRadius="6px"
        bgcolor={up ? "#ECFDF3" : "#FEF3F2"}
        color={up ? "#12B76A" : "#D92D20"}
      >
        <Icon sx={{ fontSize: 12 }} />
        <Typography fontSize={12} fontWeight={600}>
          {up ? "+" : ""}
          {change.toFixed(1)}%
        </Typography>
      </Box>
      <Typography fontSize={12} color="#667085">
        vs previous period
      </Typography>
    </Box>
  );
};

const MetricCard = ({ title, metric }: { title: string; metric?: Metric }) => (
  <Box sx={cardSx}>
    <Typography fontSize={14} color="#475467" mb="12px">
      {title}
    </Typography>
    <Typography fontSize={{ xs: 26, md: 30 }} fontWeight={600} color="#101828" mb="8px">
      {formatNaira(metric?.amount)}
    </Typography>
    <ChangeChip change={metric?.changePercent ?? null} />
  </Box>
);

const FinanceStatCards = ({
  summary,
  isLoading,
}: {
  summary?: FinanceSummary;
  isLoading: boolean;
}) => {
  const grid = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
    gap: "16px",
  };

  if (isLoading) {
    return (
      <Box sx={grid}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={130} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={grid}>
      <MetricCard title="Total Revenue" metric={summary?.totalRevenue} />
      <MetricCard title="Total Expenses" metric={summary?.totalExpenses} />
      <MetricCard title="Net Profit" metric={summary?.netProfit} />
      <Box sx={cardSx}>
        <Typography fontSize={14} color="#475467" mb="12px">
          Profit Margin
        </Typography>
        <Typography fontSize={{ xs: 26, md: 30 }} fontWeight={600} color="#101828" mb="8px">
          {Number(summary?.profitMargin?.percent || 0).toFixed(1)}%
        </Typography>
        <Typography fontSize={12} color="#667085">
          Based on selected period
        </Typography>
      </Box>
    </Box>
  );
};

export default FinanceStatCards;
